/**
 * Alert Feed Component
 * Real-time stream of trading alerts with type filtering
 */

import { useMemo, memo } from 'react';
import { AlertTriangle, TrendingUp, TrendingDown, Zap, Target, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAlertStore, type Alert, type Asset } from '@/stores/alert-store';
import { formatRelativeTime, cn } from '@/lib/utils';

interface AlertFeedProps {
  readonly asset?: Asset;
  readonly limit?: number;
  readonly selectedTypes?: ReadonlySet<string>;
  readonly externalAlerts?: readonly Alert[];
  readonly isLoading?: boolean;
  readonly onLoadMore?: () => void;
  readonly hasMore?: boolean;
}

export function AlertFeed({
  asset,
  limit = 20,
  selectedTypes,
  externalAlerts,
  isLoading = false,
  onLoadMore,
  hasMore = false,
}: AlertFeedProps) {
  const wsAlerts = useAlertStore((s) => s.alerts);

  const alerts = externalAlerts ?? wsAlerts;

  const filteredAlerts = useMemo(() => {
    let result = [...alerts];
    if (asset) {
      result = result.filter((a) => a.asset === asset);
    }
    if (selectedTypes && selectedTypes.size > 0) {
      result = result.filter((a) => selectedTypes.has(a.type));
    }
    return result.slice(0, limit);
  }, [alerts, asset, limit, selectedTypes]);

  if (isLoading && filteredAlerts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading alerts...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (filteredAlerts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            No alerts yet
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Alerts
          {asset && <Badge variant="outline">{asset}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="max-h-[600px] overflow-y-auto">
        <div className="space-y-2">
          {filteredAlerts.map((alert) => (
            <AlertItem key={alert.id} alert={alert} />
          ))}
        </div>

        {/* Load more */}
        {onLoadMore && hasMore && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={isLoading}
              className={cn(
                'rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'disabled:pointer-events-none disabled:opacity-50'
              )}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading...
                </span>
              ) : (
                'Load more'
              )}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface AlertItemProps {
  alert: Alert;
}

const AlertItem = memo(function AlertItem({ alert }: AlertItemProps) {
  const direction = alert.direction || (alert.type.includes('LONG') ? 'LONG' : alert.type.includes('SHORT') ? 'SHORT' : null);
  const isBullish = direction === 'LONG' || alert.type.includes('BUY');
  const isBearish = direction === 'SHORT' || alert.type.includes('SELL');

  const icon = useMemo(() => {
    if (alert.type.includes('CVD') || alert.type.includes('SPIKE')) {
      return <Zap className="h-4 w-4" />;
    }
    if (alert.type.includes('SIGNAL') || alert.type.includes('PIVOT')) {
      return <Target className="h-4 w-4" />;
    }
    if (isBullish) {
      return <TrendingUp className="h-4 w-4" />;
    }
    if (isBearish) {
      return <TrendingDown className="h-4 w-4" />;
    }
    return <AlertTriangle className="h-4 w-4" />;
  }, [alert.type, isBullish, isBearish]);

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border p-3 transition-colors',
        isBullish && 'border-green-500/20 bg-green-500/5',
        isBearish && 'border-red-500/20 bg-red-500/5',
        !isBullish && !isBearish && 'border-border bg-card'
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'mt-0.5',
          isBullish && 'text-green-500',
          isBearish && 'text-red-500',
          !isBullish && !isBearish && 'text-muted-foreground'
        )}
      >
        {icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge
            variant={isBullish ? 'bullish' : isBearish ? 'bearish' : 'secondary'}
            className="text-xs"
          >
            {alert.asset}
          </Badge>
          <span className="text-xs font-medium text-muted-foreground">
            {formatAlertType(alert.type)}
          </span>
          {alert.confidence && (
            <span className="text-xs text-muted-foreground">
              {Math.round(alert.confidence * 100)}%
            </span>
          )}
        </div>

        {/* Message */}
        {alert.message && (
          <p className="mt-1 text-sm text-foreground/90 line-clamp-2">
            {alert.message}
          </p>
        )}

        {/* Metadata */}
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span>{formatRelativeTime(alert.timestamp)}</span>
          {direction && (
            <>
              <span>•</span>
              <span className={isBullish ? 'text-green-500' : 'text-red-500'}>
                {direction}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

/**
 * Format alert type for display
 */
function formatAlertType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
